import React from 'react';
import { FaSync, FaInfoCircle } from 'react-icons/fa';
import NumericInput from '../../../components/NumericInput';

export type IntervalUnit = 'MONTH' | 'QUARTER' | 'YEAR';
export type AmountMode = 'FROM_TOTAL' | 'MANUAL';

export interface RecurringConfig {
  enabled: boolean;
  intervalUnit: IntervalUnit;
  intervalCount: number;
  startDate: string;
  minTermMonths: number | null;
  noticePeriodDays: number | null;
  amountMode: AmountMode;
  customAmountGross: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

interface RecurringContractSectionProps {
  config: RecurringConfig;
  onChange: (updater: (cfg: RecurringConfig) => RecurringConfig) => void;
}

export default function RecurringContractSection({
  config,
  onChange,
}: RecurringContractSectionProps): React.JSX.Element {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '24px',
        marginTop: '24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <FaSync size={18} color="#6366f1" />
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Recurring Contract
        </h3>
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#333',
        }}
      >
        <input
          type="checkbox"
          checked={config.enabled}
          onChange={(e) => onChange((cfg) => ({ ...cfg, enabled: e.target.checked }))}
          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        />
        This offer is for a recurring service
      </label>

      {config.enabled && (
        <div
          style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e0e3ef',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {/* Interval */}
          <div>
            <label style={labelStyle}>Billing Interval</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                min={1}
                value={config.intervalCount}
                onChange={(e) =>
                  onChange((cfg) => ({
                    ...cfg,
                    intervalCount: parseInt(e.target.value, 10) || 1,
                  }))
                }
                style={{ ...inputStyle, width: '70px' }}
              />
              <select
                value={config.intervalUnit}
                onChange={(e) =>
                  onChange((cfg) => ({
                    ...cfg,
                    intervalUnit: e.target.value as IntervalUnit,
                  }))
                }
                style={inputStyle}
              >
                <option value="MONTH">Month(s)</option>
                <option value="QUARTER">Quarter(s)</option>
                <option value="YEAR">Year(s)</option>
              </select>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label style={labelStyle}>Start Date</label>
            <input
              type="date"
              value={config.startDate}
              onChange={(e) =>
                onChange((cfg) => ({ ...cfg, startDate: e.target.value }))
              }
              style={inputStyle}
            />
          </div>

          {/* Minimum Term */}
          <div>
            <label style={labelStyle}>Minimum Term (months)</label>
            <input
              type="number"
              min={0}
              value={config.minTermMonths ?? ''}
              onChange={(e) =>
                onChange((cfg) => ({
                  ...cfg,
                  minTermMonths: e.target.value ? parseInt(e.target.value, 10) : null,
                }))
              }
              placeholder="e.g. 12"
              style={inputStyle}
            />
          </div>

          {/* Notice Period */}
          <div>
            <label style={labelStyle}>Notice Period (days)</label>
            <input
              type="number"
              min={0}
              value={config.noticePeriodDays ?? ''}
              onChange={(e) =>
                onChange((cfg) => ({
                  ...cfg,
                  noticePeriodDays: e.target.value ? parseInt(e.target.value, 10) : null,
                }))
              }
              placeholder="e.g. 30"
              style={inputStyle}
            />
          </div>

          {/* Amount Mode */}
          <div>
            <label style={labelStyle}>Recurring Amount</label>
            <select
              value={config.amountMode}
              onChange={(e) =>
                onChange((cfg) => ({
                  ...cfg,
                  amountMode: e.target.value as AmountMode,
                }))
              }
              style={inputStyle}
            >
              <option value="FROM_TOTAL">Use offer total</option>
              <option value="MANUAL">Custom amount</option>
            </select>
          </div>

          {/* Custom Amount (only shown when MANUAL) */}
          {config.amountMode === 'MANUAL' && (
            <div>
              <label style={labelStyle}>Custom Amount (gross)</label>
              <NumericInput
                value={config.customAmountGross}
                onChange={(val) =>
                  onChange((cfg) => ({ ...cfg, customAmountGross: val }))
                }
                placeholder="0,00"
                style={inputStyle}
              />
            </div>
          )}
        </div>
      )}

      {config.enabled && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#f0f9ff',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#0369a1',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <FaInfoCircle size={14} />
          <span>
            When this offer is accepted, you can create a service contract with these settings.
          </span>
        </div>
      )}
    </div>
  );
}
